// The notification platform's words (contracts C-4, C-6, C-7): categories,
// channels, priorities, statuses and providers as people read them. One
// place, so the inbox, the preferences and the staff pages agree.

export const CATEGORIES = ['security', 'account', 'learning', 'social', 'operational', 'product']

const CATEGORY = {
  security: { label: 'Security', help: 'New sign-ins and changes to how you sign in. Always on.' },
  account: { label: 'Account', help: 'Invitations, your organizations and your account.' },
  learning: { label: 'Learning', help: 'Courses shared with you, updates and assessments.' },
  social: { label: 'Groups', help: 'Groups you are added to.' },
  operational: { label: 'Uploads and builds', help: 'When your uploads and course builds finish.' },
  product: { label: 'Product news', help: 'News about EducationXR. Off unless you turn it on.' }
}
export const categoryLabel = c => (CATEGORY[c] ? CATEGORY[c].label : c || '')
export const categoryHelp = c => (CATEGORY[c] ? CATEGORY[c].help : '')

export const CHANNELS = ['inapp', 'email', 'push']
const CHANNEL = { inapp: 'In the app', email: 'Email', push: 'Push' }
export const channelLabel = c => CHANNEL[c] || c || ''

const PRIORITY = { low: 'Low', normal: 'Normal', high: 'High', critical: 'Critical' }
export const priorityLabel = p => PRIORITY[p] || p || ''

// Delivery statuses (C-2 §3) with the workspace pill's tone.
const DELIVERY = {
  pending: { label: 'Waiting', tone: null },
  sending: { label: 'Sending', tone: 'info' },
  retrying: { label: 'Retrying', tone: 'warn' },
  sent: { label: 'Sent', tone: 'ok' },
  captured: { label: 'Captured', tone: 'info' },
  failed: { label: 'Failed', tone: 'bad' },
  suppressed: { label: 'Suppressed', tone: 'bad' },
  skipped: { label: 'Skipped', tone: null },
  expired: { label: 'Expired', tone: 'bad' },
  parked: { label: 'Parked', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: null }
}
export const DELIVERY_STATUSES = Object.keys(DELIVERY)
export const deliveryStatusLabel = s => (DELIVERY[s] ? DELIVERY[s].label : s || 'Unknown')
export const deliveryStatusTone = s => (DELIVERY[s] ? DELIVERY[s].tone : null)

// Event statuses (notify_events).
const EVENT = {
  pending: { label: 'Waiting', tone: null },
  processed: { label: 'Processed', tone: 'ok' },
  deduped: { label: 'Duplicate', tone: null },
  skipped: { label: 'Skipped', tone: null },
  parked: { label: 'Parked', tone: 'warn' }
}
export const EVENT_STATUSES = Object.keys(EVENT)
export const eventStatusLabel = s => (EVENT[s] ? EVENT[s].label : s || 'Unknown')
export const eventStatusTone = s => (EVENT[s] ? EVENT[s].tone : null)

// Attempt outcomes (C-2).
const OUTCOME = {
  sent: { label: 'Sent', tone: 'ok' },
  captured: { label: 'Captured', tone: 'info' },
  retryable: { label: 'Will retry', tone: 'warn' },
  permanent: { label: 'Refused', tone: 'bad' },
  address_invalid: { label: 'Address no longer valid', tone: 'bad' },
  not_ready: { label: 'Channel not ready', tone: 'warn' },
  rate_limited: { label: 'Rate limited', tone: 'warn' },
  circuit_open: { label: 'Paused: provider failing', tone: 'warn' },
  expired: { label: 'Expired', tone: 'bad' },
  dry_run: { label: 'Dry run', tone: 'info' }
}
export const outcomeLabel = o => (OUTCOME[o] ? OUTCOME[o].label : o || '')
export const outcomeTone = o => (OUTCOME[o] ? OUTCOME[o].tone : null)

export const PROVIDERS = ['sendgrid', 'apns', 'fcm', 'webpush', 'capture']
const PROVIDER = { sendgrid: 'SendGrid', apns: 'Apple (APNs)', fcm: 'Firebase (FCM)', webpush: 'Web Push', capture: 'Capture (not sent)' }
export const providerLabel = p => PROVIDER[p] || p || ''

export const LANES = ['critical', 'email', 'push', 'bulk']
const LANE = { outbox: 'Outbox', events: 'Events', critical: 'Critical', email: 'Email', push: 'Push', bulk: 'Bulk' }
export const laneLabel = l => LANE[l] || l || ''

const CIRCUIT = { closed: { label: 'Closed', tone: 'ok' }, half_open: { label: 'Half open', tone: 'warn' }, open: { label: 'Open', tone: 'bad' } }
export const circuitLabel = c => (CIRCUIT[c] ? CIRCUIT[c].label : c || '')
export const circuitTone = c => (CIRCUIT[c] ? CIRCUIT[c].tone : null)

const MODE = { real: { label: 'Sending', tone: 'ok' }, capture: { label: 'Capture only', tone: 'info' }, not_ready: { label: 'Not ready', tone: 'bad' }, disabled: { label: 'Off', tone: null } }
export const providerModeLabel = m => (MODE[m] ? MODE[m].label : m || '')
export const providerModeTone = m => (MODE[m] ? MODE[m].tone : null)

// A duration in seconds, short: "45s", "12m", "3h 5m", "2d 4h".
export const durationText = seconds => {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return '—'
  const s = Math.max(0, Math.round(Number(seconds)))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
  const d = Math.floor(h / 24)
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`
}
